import { useCallback, useMemo, useState } from 'react';

import { useMaterials } from '~/api/easy-estimator-api/materials-api/hooks/useMaterials';
import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { StepName } from '~/core/enums';
import { useResetSteps } from '~/core/hooks/useResetSteps';
import type { MaterialModel } from '~/core/models';
import { useAppDispatch } from '~/redux/hooks';
import { updateSelectedMaterial } from '~/redux/slices/material';
import { useDrawingShapeContext } from '~contexts';
import { MaterialCardData } from '~molecules/MaterialCard';

type OverwritingMaterial =
  | {
      id: MaterialModel['id'];
      name: string;
    }
  | undefined;

export const useMaterialPage = () => {
  const dispatch = useAppDispatch();
  const { isFetchingMaterials, materials } = useMaterials();
  const selectedMaterial = useSelectedMaterial();
  const materialsList: MaterialCardData[] = useMemo(
    () =>
      materials.map((material) => ({
        id: material.id,
        img: { src: material.imageUrl ?? '' },
        title: material.description ?? '',
        priceRange: {
          min: material.minPrice ?? 0,
          max: material.maxPrice ?? 0,
        },
        specifications: material.attributes.map((attr) => ({
          title: attr.description,
          rating: attr.score,
        })),
        qteGrpId: material.qteGrpID,
      })),
    [materials]
  );
  const {
    getters: { areThereShapesInTheDrawingArea },
  } = useDrawingShapeContext();
  const { resetEasyEstimatorState, restoreDataWithoutAutoNavigation } =
    useResetSteps();
  const [overwritingMaterial, setOverwritingMaterial] =
    useState<OverwritingMaterial>();

  const changeSelectedMaterial = useCallback(
    (material: MaterialModel | undefined) => {
      resetEasyEstimatorState(StepName.material, {
        allowZipcodeReset: false,
        allowStoreReset: false,
      });
      restoreDataWithoutAutoNavigation(() => {
        if (material) {
          dispatch(updateSelectedMaterial(material));
        }
      });
    },
    [dispatch, restoreDataWithoutAutoNavigation, resetEasyEstimatorState]
  );

  const selectMaterialId = useCallback(
    (materialId: string) => {
      const material = materials.find(({ id }) => id === materialId);

      if (material?.id === selectedMaterial?.id) {
        return;
      }

      if (areThereShapesInTheDrawingArea) {
        setOverwritingMaterial({
          id: materialId,
          name: material?.description ?? '',
        });
      } else {
        changeSelectedMaterial(material);
      }
    },
    [
      areThereShapesInTheDrawingArea,
      changeSelectedMaterial,
      materials,
      selectedMaterial?.id,
    ]
  );

  const closeModal = useCallback(() => {
    setOverwritingMaterial(undefined);
  }, []);

  const overwriteSelectedMaterial = useCallback(() => {
    const material = materials.find(({ id }) => id === overwritingMaterial?.id);

    changeSelectedMaterial(material);
    closeModal();
  }, [materials, changeSelectedMaterial, closeModal, overwritingMaterial?.id]);

  return {
    isFetchingMaterials,
    materialsList,
    selectedMaterial,
    selectMaterialId,
    closeModal,
    overwritingMaterialName: overwritingMaterial?.name,
    overwriteSelectedMaterial,
  };
};
